import { useEffect, useState } from "react";
import { Trash2, Loader2, Flower2, Sun, Leaf, Snowflake } from "lucide-react";
import { SEASONS } from "./SeasonalHero";
import { refreshStreak } from "./Streak";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Empty, ScriptureRef } from "./Common";
import { useBibleVersion } from "@/lib/bible";

function PlanCard({ plan, onChange }: { plan: any; onChange: () => void }) {
  const [done, setDone] = useState<number[]>(plan.completed);
  const { abbr } = useBibleVersion();
  const toggle = async (day: number) => { setDone((await api.post(`/devotion-plans/${plan.id}/days/${day}/toggle`)).data.completed); refreshStreak(); };
  const del = async () => { await api.delete(`/devotion-plans/${plan.id}`); onChange(); };
  return (
    <div className="card-warm p-6" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div><h3 className="font-display text-2xl font-semibold text-slate-900">{plan.title}</h3><p className="text-sm text-slate-500">{plan.days.length} days · {plan.minutes} min/day</p></div>
        <Button variant="ghost" size="icon" data-testid={`plan-delete-${plan.id}`} onClick={del}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <Progress value={(done.length / plan.days.length) * 100} className="mt-4 h-1.5" />
      <ol className="mt-5 space-y-3">
        {plan.days.map((d: any) => (
          <li key={d.day} className="flex gap-3 rounded-xl bg-[#FAF7F2] p-4">
            <Checkbox data-testid={`plan-day-${plan.id}-${d.day}`} checked={done.includes(d.day)} onCheckedChange={() => toggle(d.day)} className="mt-1" />
            <div className="flex-1 space-y-1.5">
              <p className={`font-semibold ${done.includes(d.day) ? "text-slate-400 line-through" : "text-slate-900"}`}>Day {d.day}: {d.title}</p>
              <ScriptureRef refText={d.passage_ref} />
              {d.verse && <p className="border-l-2 border-amber-500 pl-3 font-scripture italic text-amber-950">“{d.verse}” <span className="not-italic text-xs font-semibold text-amber-800">{d.verse_ref} · {abbr}</span></p>}
              <p className="text-sm text-slate-600">{d.focus}</p>
              <p className="font-scripture italic text-slate-800">{d.prompt}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const SEASON_ICON: Record<string, any> = { spring: Flower2, summer: Sun, autumn: Leaf, winter: Snowflake };

function SeasonPlans({ onStarted }: { onStarted: () => void }) {
  const [list, setList] = useState<any[]>([]);
  const load = () => api.get("/season-plans").then((r) => setList([...r.data].sort((a, b) => Number(b.in_season) - Number(a.in_season))));
  useEffect(() => { load(); }, []);
  const start = async (id: string) => {
    try { await api.post(`/season-plans/${id}/start`); toast.success("Seasonal plan added"); load(); onStarted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section data-testid="season-plans">
      <p className="eyebrow">Seasonal devotions</p>
      <h3 className="mt-2 font-display text-3xl font-semibold text-slate-900">Ready-made plans for every season</h3>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((p) => {
          const Icon = SEASON_ICON[p.season];
          const img = SEASONS.find((s) => s.id === p.season)?.img;
          return (
            <div key={p.id} className={`group relative overflow-hidden rounded-2xl text-white ${p.in_season ? "ring-4 ring-[#C86D51]/60" : ""}`} data-testid={`season-plan-${p.id}`}>
              <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/50 to-slate-900/10" />
              <div className="relative flex h-full min-h-[240px] flex-col justify-end p-5">
                <span className="mb-auto flex w-fit items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur">
                  <Icon className="h-3.5 w-3.5" />{p.season[0].toUpperCase() + p.season.slice(1)}{p.in_season && " · In season"}
                </span>
                <h4 className="font-display text-2xl font-semibold leading-tight">{p.title}</h4>
                <p className="mt-1 text-sm text-slate-200">{p.subtitle} · {p.days.length} days</p>
                {p.started_plan_id ? <span className="mt-3 text-sm font-semibold text-amber-200" data-testid={`season-plan-started-${p.id}`}>Added to your plans ✓</span>
                  : <Button size="sm" data-testid={`season-plan-start-${p.id}`} onClick={() => start(p.id)} className="mt-3 w-fit rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">Start this plan</Button>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function DevotionPlans() {
  const [plans, setPlans] = useState<any[]>([]);
  const [f, setF] = useState({ topic: "", days: "5", minutes: "10" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/devotion-plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy(true);
    try { await api.post("/devotion-plans", { topic: f.topic, days: +f.days, minutes: +f.minutes }); setF({ ...f, topic: "" }); load(); toast.success("Your devotion plan is ready"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-12">
    <SeasonPlans onStarted={load} />
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="card-warm h-fit space-y-4 p-6 lg:col-span-4">
        <h3 className="font-display text-2xl font-semibold">Build a devotion plan</h3>
        <p className="text-sm text-slate-600">Tell us a topic: anxiety, patience, the Psalms, prayer…</p>
        <Input data-testid="plan-topic-input" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="Topic" className="bg-white" />
        <div className="grid grid-cols-2 gap-3">
          <Select value={f.days} onValueChange={(v) => setF({ ...f, days: v })}><SelectTrigger data-testid="plan-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["3", "5", "7", "14"].map((d) => <SelectItem key={d} value={d}>{d} days</SelectItem>)}</SelectContent></Select>
          <Select value={f.minutes} onValueChange={(v) => setF({ ...f, minutes: v })}><SelectTrigger data-testid="plan-minutes-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["5", "10", "15", "20"].map((d) => <SelectItem key={d} value={d}>{d} min</SelectItem>)}</SelectContent></Select>
        </div>
        <Button data-testid="plan-create-button" disabled={busy || f.topic.trim().length < 2} onClick={create} className="w-full rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing…</> : "Create plan"}
        </Button>
      </div>
      <div className="space-y-6 lg:col-span-8">
        {plans.length === 0 ? <Empty title="No plans yet" body="Your personalized devotion plans will appear here." /> : plans.map((p) => <PlanCard key={p.id} plan={p} onChange={load} />)}
      </div>
    </div>
    </div>
  );
}
